import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, get, useFormContext } from 'react-hook-form';
import { FormFields } from '../../../components/UpsertInsuranceModal/UpsertInsuranceModal.meta';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  InsuranceDTO,
  OwnerTypeDTO,
  PropertyPremiumFrequencyDTO,
  PropertyTypeDTO,
} from '../../../api/useInsuranceQuery';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { InputNumberMask } from '../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Label } from '../../../../../CommonComponents/OPL/Label/Label';
import { Radio } from '../../../../../CommonComponents/OPL/Radio/Radio';
import { Checkbox } from '../../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Textarea } from '../../../../../CommonComponents/OPL/Textarea/Textarea';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { selectCurrentClient } from '../../../../../CommonSelectors/CommonAreaSelector';

const PROPERTY_TYPES = [
  { label: 'Residential Property', value: PropertyTypeDTO.ResidentialProperty },
  { label: 'Commercial Property', value: PropertyTypeDTO.CommercialProperty },
  { label: 'Personal Property', value: PropertyTypeDTO.PersonalProperty },
  { label: 'Business Personal Property', value: PropertyTypeDTO.BusinessPersonalProperty },
  { label: 'Umbrella', value: PropertyTypeDTO.Umbrella },
  { label: 'Vehicles', value: PropertyTypeDTO.Vehicles },
];

interface Props {
  data: InsuranceDTO;
}

export const PropertyAndCasualtyDetails = ({ data }: Props) => {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormFields>();

  const ownerModel = watch('OwnerModel');

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const selectedOwnerType =
    ownerModel.length === 1 &&
    data.Options.InsuranceOwnerOptions.People.find(val => val.Name === ownerModel[0].Name)
      ?.OwnerType;

  const isPrimary = selectedOwnerType === OwnerTypeDTO.Principal;
  const isSpouse = selectedOwnerType === OwnerTypeDTO.Spouse;

  const calculateAgeLabel = (year: number) => {
    if (!Number.isInteger(year)) {
      return undefined;
    }

    if (isPrimary && primaryYob) {
      return `Age ${year - primaryYob}`;
    }

    if (isSpouse && spouseYob) {
      return `Age ${year - spouseYob}`;
    }

    return undefined;
  };

  const coverageStartOptions = data.Options.InsuranceCoverageStartYearOptions.map(year => {
    return {
      label: year.toString(),
      value: year,
      rightLabel: calculateAgeLabel(year),
    };
  });

  const coverageEndOptions = [
    { label: 'End of Plan', value: null },
    ...data.Options.InsuranceCoverageStartYearOptions.map(year => {
      return {
        label: year,
        value: year,
        rightLabel: calculateAgeLabel(year),
      };
    }),
  ];

  const propertyTypes = watch('PropertyAndCasualtyInsuranceDetails.PropertyTypes') ?? [];

  const propertyTypeOptions = useMemo(() => {
    if (propertyTypes.length === 0) {
      return PROPERTY_TYPES;
    }

    return propertyTypes.includes(PropertyTypeDTO.Umbrella)
      ? []
      : PROPERTY_TYPES.filter(({ value }) => value !== PropertyTypeDTO.Umbrella);
  }, [propertyTypes]);

  return (
    <>
      <Text fontWeight={500}>Property</Text>

      <Controller
        control={control}
        name="PropertyAndCasualtyInsuranceDetails.PropertyTypes"
        render={({ field: { value, onChange } }) => {
          return (
            <Select
              label="Type"
              value={PROPERTY_TYPES.filter(type => value?.includes(type.value))}
              options={propertyTypeOptions}
              onChange={opt => onChange(opt.map(o => o.value))}
              isMulti
              isPortal
              formVariant={
                get(errors, 'PropertyAndCasualtyInsuranceDetails.PropertyTypes')
                  ? 'error'
                  : undefined
              }
              helperText={get(errors, 'PropertyAndCasualtyInsuranceDetails.PropertyTypes')?.message}
            />
          );
        }}
      />

      <Box display="flex" gridGap="16">
        <Box flex="1">
          <Controller
            control={control}
            name="PropertyAndCasualtyInsuranceDetails.CoverageStartYear"
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  label="Coverage Start Date"
                  value={coverageStartOptions.find(opt => opt.value === value)}
                  options={coverageStartOptions}
                  onChange={opt => onChange(opt?.value)}
                  isPortal
                  formVariant={
                    get(errors, 'PropertyAndCasualtyInsuranceDetails.CoverageStartYear')
                      ? 'error'
                      : undefined
                  }
                  helperText={
                    get(errors, 'PropertyAndCasualtyInsuranceDetails.CoverageStartYear')?.message
                  }
                />
              );
            }}
          />
        </Box>
        <Box flex="1">
          {' '}
          <Controller
            control={control}
            name="PropertyAndCasualtyInsuranceDetails.CoverageEndYear"
            render={({ field: { value, onChange } }) => {
              return (
                <Select
                  label="Coverage End Date"
                  value={coverageEndOptions.find(opt => opt.value === value)}
                  options={coverageEndOptions}
                  onChange={opt => onChange(opt?.value)}
                  isPortal
                  formVariant={
                    get(errors, 'PropertyAndCasualtyInsuranceDetails.CoverageEndYear')
                      ? 'error'
                      : undefined
                  }
                  helperText={
                    get(errors, 'PropertyAndCasualtyInsuranceDetails.CoverageEndYear')?.message
                  }
                />
              );
            }}
          />
        </Box>
      </Box>

      <Controller
        control={control}
        name="PropertyAndCasualtyInsuranceDetails.Coverage"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Coverage Amount"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            maxLength={14}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={
              get(errors, 'PropertyAndCasualtyInsuranceDetails.Coverage') ? 'error' : undefined
            }
            helperText={get(errors, 'PropertyAndCasualtyInsuranceDetails.Coverage')?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="Premium"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Premium Amount"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            maxLength={14}
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={get(errors, 'Premium') ? 'error' : undefined}
            helperText={get(errors, 'Premium')?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="PropertyAndCasualtyInsuranceDetails.PremiumPaymentFrequency"
        render={({ field: { value, onChange } }) => {
          return (
            <Box role="radiogroup" aria-labelledby="insurance-premium-frequency--label">
              <Label id="insurance-premium-frequency--label" mb="4">
                Premium Payment Frequency
              </Label>

              <Box display="flex">
                <Radio
                  name="premiumPaymentFrequency"
                  checked={value === PropertyPremiumFrequencyDTO.Monthly}
                  onChange={() => onChange(PropertyPremiumFrequencyDTO.Monthly)}
                  mr="8"
                >
                  Monthly
                </Radio>

                <Radio
                  name="premiumPaymentFrequency"
                  checked={value === PropertyPremiumFrequencyDTO.Annually}
                  onChange={() => onChange(PropertyPremiumFrequencyDTO.Annually)}
                  mr="8"
                >
                  Annually
                </Radio>
              </Box>
            </Box>
          );
        }}
      />

      <Checkbox {...register('PropertyAndCasualtyInsuranceDetails.UseInflation')}>
        Increase Premium by Inflation Rate
      </Checkbox>

      <Controller
        control={control}
        name="PropertyAndCasualtyInsuranceDetails.Comment"
        render={({ field: { value, onChange } }) => {
          return (
            <Textarea
              label="Comments"
              value={value}
              onChange={onChange}
              formVariant={
                get(errors, 'PropertyAndCasualtyInsuranceDetails.Comment') ? 'error' : undefined
              }
              helperText={get(errors, 'PropertyAndCasualtyInsuranceDetails.Comment')?.message}
            />
          );
        }}
      />

      {!propertyTypes.includes(PropertyTypeDTO.Umbrella) && (
        <>
          <Separator />

          <Text fontWeight={500}>Casualty</Text>

          <Controller
            control={control}
            name="PropertyAndCasualtyInsuranceDetails.CasualtyCoverageLimit"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Coverage Limit"
                decimalScale={2}
                allowNegative={false}
                leftIcon="far fa-dollar-sign"
                maxLength={14}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                formVariant={
                  get(errors, 'PropertyAndCasualtyInsuranceDetails.CasualtyCoverageLimit')
                    ? 'error'
                    : undefined
                }
                helperText={
                  get(errors, 'PropertyAndCasualtyInsuranceDetails.CasualtyCoverageLimit')?.message
                }
              />
            )}
          />

          <Checkbox {...register('PropertyAndCasualtyInsuranceDetails.CasualtyIsLiability')}>
            Liability
          </Checkbox>
        </>
      )}
    </>
  );
};
